import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import React, { useState, useEffect } from "react";
import useDataSourceManager from "hooks/useDataSourceManager";
import { useTheme } from "@mui/material/styles";
import { Box, Button, Typography, TextField } from "@mui/material";
import { cloneDeep } from "lodash";

// Component
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import ModalContainer from "components/ModalContainer";
import { MenuSelect } from "components/Select/menuSelect";
import { RadioButton } from 'components/buttons/RadioButton';
import GlobalAlert from "components/Notification";
import Loading from "./loading";

// Util
import {
  defaultValue,
  deviceCategoryOption,
  portTypeOption,
  linkSpeedOption,
} from "./util/const";
import Api from "./util/api";
import Form from "./util/form";

// Const
import { formLimitation } from 'const';

const PortProfileModal = ({
  auth,
  uuidList,
  openModal,
  handleCloseModal,
  onSaveChanges,
}) => {
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const api = Api.init(auth);
  const form = Form.init(theme);

  // State
  const [loading, setLoading] = useState(true);
  const [formValue, setFormValue] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  // Method
  const init = () => {
    setLoading(true);
    setFormValue(cloneDeep(defaultValue));
    setLoading(false);
  }

  const handleChangeFormValue = (key, value) => {
    setFormValue((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const checkFormValid = () => {
    if (!formValue.profileName || formValue.profileName.trim() === "") {
      return false;
    }

    if (
      (formValue.deviceCategory === deviceCategoryOption.ap.value)
      || (
        formValue.deviceCategory === deviceCategoryOption.switch.value
        && formValue.portType === portTypeOption.trunk.value
      )
    ) {
      if (!form.checkAllowVlans(formValue.allowVlan?.trim())) {
        return false;
      }
    }

    if (!form.checkPvid(formValue.pvid?.toString().trim())) {
      return false;
    }

    return true;
  }

  const handleSave = () => {
    setLoading(true);
    const payload = defaultValue.makePayload(formValue);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.create);
    sendRequest(
      apiKey,
      uuidList.create,
      params,
      payload,
    );
  }

  // Side effect
  useEffect(() => {
    if (openModal) {
      init();
    } else {
      setFormValue(cloneDeep(defaultValue));
    }
  }, [openModal]);

  useEffect(() => {
    const createResponse = getData(uuidList.create);

    if (createResponse) {
      if (createResponse.hasOwnProperty("code") && createResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: createResponse?.message, // TODO: not define error message format
        });
        setLoading(false);
      } else {
        onSaveChanges();
      }
    }
  }, [dataMap]);

  if (!openModal) {
    return null;
  }

  return (
    <>
      <ModalContainer display={openModal} modalWidth="auto">
        {loading && (
          <Loading />
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            Create New Port Profile
          </Typography>
          <Button
            onClick={handleCloseModal}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
        {/* Device Type */}
        <Box sx={form.getRowStyle()}>
          <Box sx={form.getLabelStyle()}>Device Type</Box>
          {
            deviceCategoryOption.convertToForm().map((item, index) => (
              <RadioButton
                key={index}
                label={item.label}
                value={item.value}
                checked={formValue.deviceCategory === item.value}
                onChange={() => handleChangeFormValue("deviceCategory", item.value)}
                name="deviceCategory"
              />
            ))
          }
        </Box>
        {/* Profile Name */}
        <Box sx={form.getRowStyle()}>
          <Box sx={form.getLabelStyle()}>Profile Name</Box>
          <TextField
            placeholder="e.g. My Office"
            sx={form.getTextfieldStyle(true)}
            value={formValue.profileName}
            onChange={(e) => handleChangeFormValue("profileName", e.target.value)}
          />
        </Box>
        {/* Port State */}
        {
          formValue.deviceCategory === deviceCategoryOption.switch.value && (
            <>
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Port State</Box>
                <RadioButton
                  label="Enabled"
                  value={true}
                  checked={formValue.portState === true}
                  onChange={() => handleChangeFormValue("portState", true)}
                  name="portState"
                />
                <RadioButton
                  label="Disabled"
                  value={false}
                  checked={formValue.portState === false}
                  onChange={() => handleChangeFormValue("portState", false)}
                  name="portState"
                />
              </Box>
              {/* Port Type */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Port Type</Box>
                {
                  portTypeOption.convertToForm().map((item, index) => (
                    <RadioButton
                      key={index}
                      label={item.label}
                      value={item.value}
                      checked={formValue.portType === item.value}
                      onChange={() => handleChangeFormValue("portType", item.value)}
                      name="portType"
                    />
                  ))
                }
              </Box>
            </>
          )
        }
        {/* PVID */}
        <Box sx={form.getRowStyle()}>
          <Box sx={form.getLabelStyle()}>PVID</Box>
          <TextField
            sx={form.getTextfieldStyle(!!!formValue.pvid ? true : form.checkPvid(formValue.pvid?.toString().trim()))}
            value={formValue.pvid}
            onChange={(e) => { handleChangeFormValue("pvid", e.target.value); }}
          />
          <Typography
            sx={{
              color: theme.palette.primary.greySecound,
              fontSize: theme.typography.customFontSize[1],
            }}
          >
            (1~4094)
          </Typography>
        </Box>
        {
          (
            (formValue.deviceCategory === deviceCategoryOption.ap.value)
            || (
              formValue.deviceCategory === deviceCategoryOption.switch.value
              && formValue.portType === portTypeOption.trunk.value
            )
          ) && (
            <>
              {/* Allowed VLANs */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Allowed VLANs</Box>
                <TextField
                  placeholder="e.g. 1,4-7"
                  sx={form.getTextfieldStyle(!!!formValue.allowVlan ? true : form.checkAllowVlans(formValue.allowVlan?.trim()))}
                  value={formValue.allowVlan}
                  onChange={(e) => handleChangeFormValue("allowVlan", e.target.value)}
                />
                <Typography
                  sx={{
                    color: theme.palette.primary.greySecound,
                    fontSize: theme.typography.customFontSize[1],
                  }}
                >
                  {/* (1~4094) */}
                  {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                </Typography>
              </Box>
            </>
          )
        }
        {
          formValue.deviceCategory === deviceCategoryOption.switch.value && (
            <>
              {/* Copper Link Speed */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Copper Link Speed </Box>
                <MenuSelect
                  value={formValue.linkSpeed}
                  onChange={(opt) => {
                    handleChangeFormValue("linkSpeed", opt.value);
                  }}
                  options={linkSpeedOption.convertToForm()}
                  width={form.getTextfieldStyle().width}
                  additionZIndex={1}
                />
              </Box>
              {/* Fiber Link Speed */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>Fiber Link Speed </Box>
                <MenuSelect
                  value={formValue.fiberLinkSpeed}
                  onChange={(opt) => {
                    handleChangeFormValue("fiberLinkSpeed", opt.value);
                  }}
                  options={linkSpeedOption.convertToForm()}
                  width={form.getTextfieldStyle().width}
                />
              </Box>
              {/* DDP State */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>DDP</Box>
                <RadioButton
                  label="Enabled"
                  value={true}
                  checked={formValue.ddpState === true}
                  onChange={() => handleChangeFormValue("ddpState", true)}
                  name="ddpState"
                />
                <RadioButton
                  label="Disabled"
                  value={false}
                  checked={formValue.ddpState === false}
                  onChange={() => handleChangeFormValue("ddpState", false)}
                  name="ddpState"
                />
              </Box>
              {/* LBD  */}
              <Box sx={form.getRowStyle()}>
                <Box sx={form.getLabelStyle()}>LBD</Box>
                <RadioButton
                  label="Enabled"
                  value={true}
                  checked={formValue.lbdState === true}
                  onChange={() => handleChangeFormValue("lbdState", true)}
                  name="lbdState"
                />
                <RadioButton
                  label="Disabled"
                  value={false}
                  checked={formValue.lbdState === false}
                  onChange={() => handleChangeFormValue("lbdState", false)}
                  name="lbdState"
                />
              </Box>
            </>
          )
        }
        <Box
          sx={{
            margin: "24px 0 16px auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <OutlineButton
            onClick={handleCloseModal}
            sx={{ marginRight: "20px" }}
            width="100px"
            height="32px"
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleSave}
            width="100px"
            height="32px"
            disabled={!checkFormValid()}
          >
            Create
          </SolidButton>
        </Box>
      </ModalContainer>
      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default PortProfileModal;